/*
 * section-collection-pillar.js
 *
 * Project 08 — Concern collection (pillar) page interactivity.
 *
 * Two behaviors:
 *   1. Symptom-spectrum interactive checklist (gated by data-spectrum-mode="interactive")
 *      Reads [data-spectrum-item] elements, toggles aria-checked on click/Enter/Space,
 *      updates [data-spectrum-count] and [data-spectrum-total], swaps band copy by tally.
 *   2. Okendo placeholder swap — if the Okendo review carousel renders <3 review cards
 *      within a poll window, removes the [hidden] attribute on [data-placeholder-for-okendo]
 *      so the canon.reviews placeholder takes over.
 *
 * Vanilla JS. No jQuery. Single IIFE. Initialised on DOMContentLoaded.
 * Re-init on Shopify section reload (section:load event) for theme editor.
 *
 * See: docs/plans/project-08-implementation.md (PR 3b)
 */

(function () {
  'use strict';

  // ─── Spectrum interactive ──────────────────────────────────────────────────
  function initSpectrum(root) {
    const spectrum = root.querySelector('.collection-pillar__spectrum[data-spectrum-mode="interactive"]');
    if (!spectrum) return;

    const items = Array.from(spectrum.querySelectorAll('[data-spectrum-item]'));
    if (items.length === 0) return;

    const countEl = spectrum.querySelector('[data-spectrum-count]');
    const totalEl = spectrum.querySelector('[data-spectrum-total]');
    const copyEl = spectrum.querySelector('[data-spectrum-band-copy]');
    const ctaEl = spectrum.querySelector('[data-spectrum-cta]');
    const ctaTextEl = spectrum.querySelector('[data-spectrum-cta-text]');

    if (totalEl) totalEl.textContent = String(items.length);

    // CTA upgrade config — when count crosses threshold, swap href + label
    // from lead bundle to the upgrade bundle. Only active if data-spectrum-cta-upgrade-url
    // is present on the CTA (digestion concern wires this; others may not).
    const ctaConfig = ctaEl ? {
      leadUrl:      ctaEl.getAttribute('data-spectrum-cta-lead-url')      || ctaEl.getAttribute('href') || '',
      leadLabel:    ctaEl.getAttribute('data-spectrum-cta-lead-label')    || (ctaTextEl ? ctaTextEl.textContent : ''),
      upgradeUrl:   ctaEl.getAttribute('data-spectrum-cta-upgrade-url')   || '',
      upgradeLabel: ctaEl.getAttribute('data-spectrum-cta-upgrade-label') || '',
      threshold:    parseInt(ctaEl.getAttribute('data-spectrum-cta-upgrade-threshold') || '9', 10),
    } : null;

    function getCount() {
      return items.reduce((n, item) => n + (item.getAttribute('aria-checked') === 'true' ? 1 : 0), 0);
    }

    function bandCopy(count, upgradeActive) {
      if (count === 0) {
        return 'Tick the items you recognise. Most customers tick somewhere between <strong>3 and 8</strong>. None of these are wrong — they’re just different starting points.';
      }
      if (count < 4) {
        return '<strong>A lighter pattern.</strong> A few signals, but the protocol still meets you where you are. Start where it feels easiest.';
      }
      if (count <= 8) {
        return '<strong>The most common range.</strong> 4–8 ticks is where most customers land. The lead bundle is built for exactly this terrain.';
      }
      if (upgradeActive) {
        return '<strong>A broader spectrum.</strong> A larger pattern is showing up. <em>Daily+</em> carries the protocol at full depth for exactly this — and the practitioner network is there for a clinical conversation.';
      }
      return '<strong>A broader spectrum.</strong> A larger pattern is showing up. The lead bundle still routes through correctly — and the practitioner network is there if you want a clinical conversation.';
    }

    function swapCta(toUpgrade) {
      if (!ctaEl || !ctaConfig) return;
      const url   = toUpgrade && ctaConfig.upgradeUrl   ? ctaConfig.upgradeUrl   : ctaConfig.leadUrl;
      const label = toUpgrade && ctaConfig.upgradeLabel ? ctaConfig.upgradeLabel : ctaConfig.leadLabel;
      ctaEl.setAttribute('href', url);
      if (ctaTextEl) ctaTextEl.textContent = label;
    }

    function update() {
      const count = getCount();
      const upgradeAvailable = ctaConfig && ctaConfig.upgradeUrl !== '';
      const upgradeActive = upgradeAvailable && count >= ctaConfig.threshold;
      if (countEl) countEl.textContent = String(count);
      if (copyEl) copyEl.innerHTML = bandCopy(count, upgradeActive);
      swapCta(upgradeActive);
    }

    function toggle(item) {
      const next = item.getAttribute('aria-checked') !== 'true';
      item.setAttribute('aria-checked', next ? 'true' : 'false');
      update();
    }

    items.forEach((item) => {
      item.addEventListener('click', () => toggle(item));
      item.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle(item);
        }
      });
    });

    update();
  }

  // ─── Okendo placeholder swap ───────────────────────────────────────────────
  // Okendo embeds render asynchronously; there's no public ready event we can
  // hook. Strategy: poll for rendered carousel content for up to ~3 seconds,
  // then settle the placeholder state. If fewer than data-okendo-min-count
  // review cards rendered, reveal the placeholder.
  function initOkendoSwap(root) {
    const blocks = Array.from(root.querySelectorAll('.collection-pillar__reviews-block'));
    if (blocks.length === 0) return;

    blocks.forEach((block) => {
      const placeholder = block.querySelector('[data-placeholder-for-okendo]');
      if (!placeholder) return;

      const minCount = parseInt(block.getAttribute('data-okendo-min-count') || '3', 10);
      const carousel = block.querySelector('[data-oke-reviews-carousel]');
      if (!carousel) {
        // No Okendo container at all — reveal placeholder immediately.
        placeholder.hidden = false;
        placeholder.removeAttribute('aria-hidden');
        return;
      }

      let polls = 0;
      const maxPolls = 12;
      const pollInterval = 250;

      function countReviewCards() {
        // Okendo renders cards inside the carousel container. We count anchor
        // descendants which Okendo uses for review tiles; fall back to any
        // direct children if Okendo's class names change in the future.
        const okeCards = carousel.querySelectorAll(
          '[data-oke-reviews-review], .oke-reviews-reviewContent, .oke-w-reviews-list-item'
        );
        if (okeCards.length > 0) return okeCards.length;
        // Fallback heuristic: count direct element children after Okendo populates.
        return carousel.children.length;
      }

      function settle() {
        const count = countReviewCards();
        if (count >= minCount) {
          // Okendo carries the section — keep placeholder hidden.
          placeholder.hidden = true;
          placeholder.setAttribute('aria-hidden', 'true');
        } else {
          // Reveal placeholder.
          placeholder.hidden = false;
          placeholder.removeAttribute('aria-hidden');
        }
      }

      const interval = setInterval(() => {
        polls += 1;
        if (countReviewCards() >= minCount) {
          clearInterval(interval);
          settle();
          return;
        }
        if (polls >= maxPolls) {
          clearInterval(interval);
          settle();
        }
      }, pollInterval);
    });
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  function init(root) {
    if (!root) return;
    initSpectrum(root);
    initOkendoSwap(root);
  }

  function bootstrap() {
    const roots = document.querySelectorAll('.collection-pillar');
    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Shopify theme editor section reload.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const root = section.querySelector('.collection-pillar');
    if (root) init(root);
  });
})();
